import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Play, PlayCircle, Timer, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playBigCheers } from "@/lib/sfx";
import { playReadingAudio, playSay, playSayHighlight, stopReading } from "@/lib/readingAudio";
import KikoCheer from "@/components/KikoCheer";
import ExerciseRunner from "@/components/reading/ExerciseRunner";
import FlashCards from "@/components/reading/FlashCards";
import StoryRecorder from "@/components/reading/StoryRecorder";
import { Btn } from "@/pages/ReadingHome";
import { isReadingApp } from "@/lib/readingApp";

export const L3_LINES = {
  fr: { go: "C'est parti !", words: "Lis les mots", phrases: "Lis les phrases", text: "Le petit texte", newWords: "Mots nouveaux", listen: "Kiko lit", start: "Je commence à lire",
    done: "J'ai fini", next: "Suivant", tries: ["1re lecture : doucement", "2e lecture : plus vite", "3e lecture : comme un acteur !"],
    win: "Bravo ! Tu as lu le texte en moins de 2 minutes !", again: "Encore un essai ! Lis le texte en moins de 2 minutes.", practice: "Fais d'abord les exercices.",
    fast: "Oh là, trop vite ! Lis bien chaque mot, puis appuie sur J'ai fini." },
  en: { go: "Let's go!", words: "Read the words", phrases: "Read the sentences", text: "The little text", newWords: "New words", listen: "Kiko reads", start: "Start reading",
    done: "I finished", next: "Next", tries: ["1st read: slowly", "2nd read: faster", "3rd read: like an actor!"],
    win: "Well done! You read the text in under 2 minutes!", again: "One more try! Read the text in under 2 minutes.", practice: "Do the exercises first.",
    fast: "Whoa, too fast! Read every word, then press I finished." },
};
// Kiko's answer after a timed reading
export const readMsg = (t, r) => (r.passed ? t.win : r.too_fast ? t.fast : r.need_practice ? t.practice : t.again);
const STEPS = ["intro", "words", "cards", "phrases", "practise", "text"];

function Sentence({ text, lang, testid }) {
  const [hi, setHi] = useState(-1);
  return (
    <button onClick={() => playSayHighlight(text, lang, setHi)} className="text-left text-2xl sm:text-3xl font-bold leading-relaxed flex flex-wrap gap-x-2 items-center" data-testid={testid}>
      <Volume2 className="text-blue-600 shrink-0" />
      {text.split(" ").map((w, k) => <span key={k} className={`rounded-lg px-1 transition-colors duration-100 ${hi === k ? "bg-yellow-200 text-blue-700" : ""}`}>{w}</span>)}
    </button>
  );
}

function Card({ children }) {
  return <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-white border-[3px] border-slate-900 p-6 sm:p-8 shadow-[6px_6px_0_0_#0F172A] space-y-6">{children}</motion.div>;
}

function TextStep({ d, childId, lessonId, lang, onResult }) {
  const t = L3_LINES[lang];
  const l3 = d.lesson.l3;
  const [tryN, setTryN] = useState(0);
  const [start, setStart] = useState(null);
  const [now, setNow] = useState(0);
  const tick = useRef(null);
  useEffect(() => () => clearInterval(tick.current), []);
  const begin = () => { stopReading(); setStart(Date.now()); setNow(Date.now()); tick.current = setInterval(() => setNow(Date.now()), 250); };
  const finish = async () => {
    clearInterval(tick.current);
    const secs = Math.round((Date.now() - start) / 1000);
    setStart(null);
    try {
      const { data } = await api.post(`/reading/${childId}/lesson/${lessonId}/text-read`, { seconds: secs });
      onResult(data);
      setTryN((n) => Math.min(n + 1, 2));
    } catch { toast.error(lang === "fr" ? "Oups, réessaie." : "Oops, try again."); }
  };
  const secs = start ? Math.floor((now - start) / 1000) : 0;
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2" data-testid="l3-new-words">
        <span className="font-bold text-slate-500">{t.newWords} :</span>
        {l3.new_words.map((w) => <button key={w} onClick={() => playSay(w, lang)} className="rounded-full bg-amber-200 border-2 border-slate-900 px-3 py-1 font-bold" data-testid={`l3-new-word-${w}`}>{w}</button>)}
      </div>
      <h2 className="text-2xl sm:text-3xl font-bold" data-testid="l3-text-title">{l3.text_title}</h2>
      <p className="text-xl sm:text-2xl leading-loose" data-testid="l3-text">{l3.text}</p>
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full bg-slate-900 text-white px-4 py-2 font-bold" data-testid="l3-try">{t.tries[tryN]}</span>
        {start ? (
          <>
            <span className={`text-3xl font-bold tabular-nums ${secs > 120 ? "text-red-600" : "text-green-700"}`} data-testid="l3-timer"><Timer className="inline -mt-1" /> {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</span>
            <Btn onClick={finish} testid="l3-text-done" color="bg-green-500 text-white">{t.done}</Btn>
          </>
        ) : (
          <>
            <Btn onClick={begin} testid="l3-text-start" color="bg-blue-600 text-white"><Play size={18} /> {t.start}</Btn>
            <Btn onClick={() => playSayHighlight(l3.text, lang, () => {})} testid="l3-text-listen"><Volume2 size={18} /> {t.listen}</Btn>
          </>
        )}
      </div>
      <StoryRecorder childId={childId} storyId={lessonId} page={0} fr={lang === "fr"} />
    </Card>
  );
}

export const DEMO_MORE = {
  fr: "Il y a encore des leçons, des vidéos, des flash cards et des défis. Demande à tes parents de débloquer la Lecture : incluse dans les plans 3 matières, toutes matières et le Holiday Pack, ou Rs 200 / mois.",
  en: "There are more lessons with videos, flash cards and challenges. Ask your parent to unlock Reading: included in the 3-subject, all-subject and Holiday Pack plans, or Rs 200 / month.",
};
const VIDEO_TAB = { son: { fr: "Le son", en: "The sound" }, lecture: { fr: "La leçon", en: "The lesson" } };

// Optional Kiko videos of the lesson (sound, then reading): the child can watch them or go straight on
function L3Videos({ videos, lang }) {
  const [v, setV] = useState(0);
  if (!videos?.length) return null;
  return (
    <div className="space-y-3" data-testid="l3-videos">
      <video key={videos[v].src} src={videos[v].src} controls controlsList="nodownload" disablePictureInPicture playsInline className="w-full rounded-2xl border-[3px] border-slate-900 bg-black" data-testid="l3-video" />
      <div className="flex flex-wrap gap-2">
        {videos.map((x, k) => (
          <button key={x.kind} onClick={() => { stopReading(); setV(k); }} data-testid={`l3-video-tab-${x.kind}`}
            className={`rounded-full border-2 border-slate-900 px-4 h-10 font-bold transition-colors ${k === v ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-100"}`}>
            <PlayCircle size={16} className="inline -mt-0.5 mr-1" />{VIDEO_TAB[x.kind][lang]}
          </button>
        ))}
      </div>
    </div>
  );
}

function DemoEnd({ lang }) {
  const navigate = useNavigate();
  const fr = lang === "fr";
  return (
    <Card>
      <div className="flex flex-col items-center gap-4 text-center" data-testid="l3-demo-end">
        <img src="/mascot/kiko.png" alt="Kiko" className="w-32 h-32" />
        <h2 className="text-3xl font-bold">{fr ? "Bravo ! Tu as fini l'essai du Niveau 3 !" : "Well done! You finished the Level 3 trial!"}</h2>
        <p className="font-semibold text-lg max-w-lg">{DEMO_MORE[lang]}</p>
        {!isReadingApp() && <Btn onClick={() => navigate("/pricing?reading=1")} testid="l3-demo-unlock" color="bg-yellow-300">{fr ? "Débloquer la Lecture" : "Unlock Reading"}</Btn>}
      </div>
    </Card>
  );
}

// Level 3 lesson: Kiko intro + videos -> 10 words -> flash cards -> 3 sentences -> exercises -> short text read in under 2 minutes.
export default function Level3Lesson({ d, childId, lessonId, lang, petit }) {
  const navigate = useNavigate();
  const t = L3_LINES[lang];
  const l3 = d.lesson.l3;
  const [step, setStep] = useState("intro");
  const [kiko, setKiko] = useState(null);
  const [res, setRes] = useState(null);
  const [session] = useState(() => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);
  const steps = STEPS.filter((s) => (s !== "cards" || d.cards.length) && (s !== "text" || !d.demo));
  const go = (s) => { stopReading(); setStep(s); };
  const afterPractice = async () => {
    await api.post(`/reading/${childId}/lesson/${lessonId}/practice`, { session }).catch(() => {});
    if (d.demo) { playBigCheers(); go("demo_end"); return; }
    go("text");
  };
  const onResult = (r) => {
    setRes(r);
    if (r.passed) { playBigCheers(); setKiko({ n: Date.now(), msg: t.win, kind: "win", streak: 10 }); playSay(t.win, lang); }
    else playSay(readMsg(t, r), lang);
  };
  return (
    <div className="max-w-4xl mx-auto px-5 py-6 space-y-6" data-testid="l3-lesson">
      <div className="flex gap-2" data-testid="l3-steps">
        {steps.map((s) => <button key={s} onClick={() => go(s)} className={`h-2.5 flex-1 rounded-full ${s === step || step === "demo_end" ? "bg-blue-600" : "bg-slate-300"}`} data-testid={`l3-step-${s}`} aria-label={s} />)}
      </div>
      {step === "intro" && (
        <Card>
          <L3Videos videos={d.l3_videos} lang={lang} />
          <div className="flex flex-wrap gap-3" data-testid="l3-graphemes">
            {l3.graphemes.map((g) => <span key={g} className="rounded-2xl bg-yellow-300 border-[3px] border-slate-900 px-5 py-3 text-3xl sm:text-4xl font-bold">{g}</span>)}
          </div>
          <div className="flex items-end gap-3">
            <img src="/mascot/kiko.png" alt="Kiko" className={petit ? "w-28 h-28" : "w-20 h-20"} />
            <Sentence text={l3.kiko} lang={lang} testid="l3-kiko-line" />
          </div>
          <Btn onClick={() => go("words")} testid="l3-go" color="bg-blue-600 text-white">{t.go} <ArrowRight size={18} /></Btn>
        </Card>
      )}
      {step === "words" && (
        <Card>
          <h2 className="text-2xl font-bold">{t.words}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {l3.words.map((w, k) => <motion.button key={w} whileTap={{ scale: 0.95 }} onClick={() => playReadingAudio(w, lang)} data-testid={`l3-word-${k}`}
              className="rounded-2xl bg-sky-100 border-[3px] border-slate-900 py-4 text-2xl font-bold">{w}</motion.button>)}
          </div>
          <Btn onClick={() => go(d.cards.length ? "cards" : "phrases")} testid="l3-words-next" color="bg-blue-600 text-white">{t.next} <ArrowRight size={18} /></Btn>
        </Card>
      )}
      {step === "cards" && <FlashCards cards={d.cards} lang={lang} petit={petit} onDone={() => go("phrases")} />}
      {step === "phrases" && (
        <Card>
          <h2 className="text-2xl font-bold">{t.phrases}</h2>
          {l3.phrases.map((p, k) => <Sentence key={p} text={p} lang={lang} testid={`l3-phrase-${k}`} />)}
          <Btn onClick={() => go("practise")} testid="l3-phrases-next" color="bg-blue-600 text-white">{t.next} <ArrowRight size={18} /></Btn>
        </Card>
      )}
      {step === "practise" && <ExerciseRunner key="l3" session={session} childId={childId} lang={lang} petit={petit} items={d.exercises} onDone={afterPractice} />}
      {step === "demo_end" && <DemoEnd lang={lang} />}
      {step === "text" && !d.demo && <TextStep d={d} childId={childId} lessonId={lessonId} lang={lang} onResult={onResult} />}
      {res && step === "text" && (
        <div className={`rounded-3xl border-[3px] border-slate-900 p-5 font-bold text-lg ${res.passed ? "bg-green-200" : "bg-amber-100"}`} data-testid="l3-result">
          <p>{readMsg(t, res)} ({Math.floor(res.seconds / 60)}:{String(res.seconds % 60).padStart(2, "0")})</p>
          <div className="flex flex-wrap gap-3 mt-3">
            {res.need_practice && <Btn onClick={() => go("practise")} testid="l3-to-practise">{lang === "fr" ? "Aux exercices" : "To the exercises"}</Btn>}
            {res.certificate && <Btn onClick={() => navigate(`/reading-certificate/${childId}/${res.certificate.lang}/${res.certificate.level}`)} testid="l3-certificate" color="bg-amber-400"><Award size={18} /> {lang === "fr" ? "Mon certificat" : "My certificate"}</Btn>}
            {res.next_lesson && <Btn onClick={() => navigate(`/kid/${childId}/reading/lesson/${res.next_lesson}`)} testid="l3-next-lesson" color="bg-blue-600 text-white">{t.next} <ArrowRight size={18} /></Btn>}
          </div>
        </div>
      )}
      <KikoCheer cheer={kiko} />
    </div>
  );
}
